import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { getUserPermissions } from '@utils/userPermissions'
import type React from 'react'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { FORM_PERSISTENCE_STORAGE_PREFIX } from '@/hooks/form/formPersistenceStorage'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import type { UserTenant } from '@/types/Organization'

const TENANT_STORAGE_KEY = 'selectedTenant'

// Define context type inline to avoid circular dependencies
interface TenantContextType {
  currentTenant: UserTenant
  availableTenants: UserTenant[]
  switchTenant: (tenantId: UserTenant['id']) => Promise<void>
  isLoading: boolean
  isVikenSkog: boolean
  isSBSkog: boolean
  permissionsLoading: boolean
}

// All possible tenants
const ALL_TENANTS: UserTenant[] = [
  {
    id: 'VikenSkog',
    name: 'Viken Skog',
    displayName: 'Viken Skog',
  },
  {
    id: 'SBSkog',
    name: 'SB Skog',
    displayName: 'SB Skog',
  },
]

const TenantContext = createContext<TenantContextType | undefined>(undefined)

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [permissionsLoading, setPermissionsLoading] = useState(true)
  const [availableTenants, setAvailableTenants] = useState<UserTenant[]>([])

  // Store selected tenant in localStorage - default will be set after permissions load
  const [selectedTenantId, setSelectedTenantId] = useLocalStorage<UserTenant['id'] | undefined>(
    TENANT_STORAGE_KEY,
    undefined,
  )

  // Load permissions and filter available tenants
  useEffect(() => {
    void getUserPermissions().then((perms) => {
      const accessibleTenants = ALL_TENANTS.filter((tenant) => perms[`has${tenant.id}Access`])

      setAvailableTenants(accessibleTenants)

      // If selected tenant is not accessible or not set, switch to first available
      if (accessibleTenants.length > 0) {
        const isCurrentAccessible =
          selectedTenantId && accessibleTenants.some((t) => t.id === selectedTenantId)
        if (!isCurrentAccessible) {
          console.warn(
            `Selected tenant ${selectedTenantId ?? 'none'} not accessible, switching to ${accessibleTenants[0].id}`,
          )
          setSelectedTenantId(accessibleTenants[0].id)
        }
      }

      setPermissionsLoading(false)
    })
  }, [selectedTenantId, setSelectedTenantId])

  const currentTenant =
    availableTenants.find((tenant) => tenant.id === selectedTenantId) ??
    availableTenants.at(0) ??
    ALL_TENANTS[0]

  const clearTenantSpecificUiState = (): void => {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(FORM_PERSISTENCE_STORAGE_PREFIX))
      .forEach((key) => {
        localStorage.removeItem(key)
      })
  }

  const switchTenant = async (tenantId: UserTenant['id']) => {
    if (tenantId === currentTenant.id) return

    setIsLoading(true)

    try {
      // Update the selected tenant
      setSelectedTenantId(tenantId)

      // Clear all cached queries since we're switching tenant context
      queryClient.clear()

      // Optionally invalidate specific queries that are tenant-dependent
      await queryClient.invalidateQueries({
        predicate: (query) => {
          // Invalidate queries that are likely tenant-specific
          const queryKey = query.queryKey[0] as string
          return ['forestOwners', 'forestOwnerGroups', 'tenants'].includes(queryKey)
        },
      })

      // Navigate to home page after tenant switch since all data is tenant-specific
      await router.navigate({ to: '/search' })
      clearTenantSpecificUiState()
    } catch (error) {
      console.error('Failed to switch tenant:', error)
      // Revert to previous tenant on error
      setSelectedTenantId(currentTenant.id)
    } finally {
      setIsLoading(false)
    }
  }

  const contextValue: TenantContextType = {
    currentTenant,
    availableTenants,
    switchTenant,
    isLoading,
    isVikenSkog: currentTenant.id === 'VikenSkog',
    isSBSkog: currentTenant.id === 'SBSkog',
    permissionsLoading,
  }

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>
}

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
